/**
 * Content System Initialization
 *
 * Single async entry point that loads all seed data and builds question indexes.
 * Call once at app startup before accessing any question data.
 *
 * Bundle Optimization Phase 2 — mei 2026.
 */

import { initializeSeedCollections } from './seedConsolidator'
import { initializeQuestionLoader } from './questionLoader'
import { debug, error as logError } from '../../utils/logger'

let _initPromise: Promise<void> | null = null

/**
 * Initialize the entire content system:
 * 1. Load all seed JSON files via dynamic import (per-module chunks)
 * 2. Build SEED_COLLECTIONS with validation
 * 3. Build ALL_QUESTIONS_CORE + all indexes
 *
 * Idempotent — safe to call multiple times, only runs once.
 * Returns the same promise if called concurrently.
 */
export async function initializeContentSystem(): Promise<void> {
  if (_initPromise) return _initPromise

  _initPromise = (async () => {
    const start = performance.now()

    // Step 1+2: Load seeds and build collections
    await initializeSeedCollections()

    // Step 3: Build question indexes
    await initializeQuestionLoader()

    const elapsed = Math.round(performance.now() - start)
    debug(`✅ Content system initialized in ${elapsed}ms`)
  })()

  try {
    await _initPromise
  } catch (err) {
    // Reset so it can be retried
    _initPromise = null
    logError('❌ Content system initialization failed:', err)
    throw err
  }
}
